<template>
	<main v-if="!loading" class="organization-settings-change-plan relative">
		<div class="change-plan__container">
			<section class="flex gap-4 items-start">
				<CPlanCard
					:plan="{
						...viewModel.PlanPricingCardViewModel.Value,
						Product: viewModel.PlanPricingCardViewModel.Value
					}"
					:show-custom-description="false"
					class="change-plan__card border border-subtle shrink-0 content print:hidden shadow"
				>
					<template #custom-title>
						{{ viewModel.PlanPricingCardViewModel.Value.DisplayName }}
					</template>

					<template v-if="featuresLosing && featuresLosing.length" #features-losing>
						<ul class="text-subtle pt-4">
							<li v-for="feature in visibleFeaturesLosing" :key="feature">
								<span class="line-through">{{ feature }}</span>
							</li>
							<CExpandable :expanded="showAllFeaturesLosing">
								<li v-for="feature in overflowFeaturesLosing" :key="feature">
									<span class="line-through">{{ feature }}</span>
								</li>
							</CExpandable>
							<button
								v-if="allowToggleFeaturesLosing"
								class="flex flex-col items-center button button--no-style sz-2 mt-4 mx-auto heavy hover:text-accent-7"
								:class="{ 'flex-col-reverse' : showAllFeaturesLosing, 'gap-2' : !showAllFeaturesLosing }"
								@click="showAllFeaturesLosing = !showAllFeaturesLosing"
							>
								<span>{{ showMoreText }}</span>
								<Inline :svg="showAllFeaturesLosing ? 'chevron-up' : 'chevron-down'" />
							</button>
						</ul>
					</template>
				</CPlanCard>
				<div class="flex-1">
					<DowngradeSurvey
						v-if="viewModel.NewPlan.Name === 'Individual'"
						:button-status="downgradeButtonStatus"
						:use-new-billing="useNewBilling"
						:view-model="viewModel ? viewModel.DowngradeFeedback : null"
						@submit="submitDowngradeFeedback"
					/>
					<ValidationObserver v-else ref="observer">
						<form @submit.prevent="() => updatePlan(isCustomerAddressValidationEnabled)">
							<template v-if="publishableKey">
								<h3 class="font-display text-4 mb-3">Billing Information</h3>
								<SubscriptionBilling
									ref="subscriptionBilling"
									data-testid="subscription-billing"
									:stripe-key="publishableKey"
									:subscription-view-model="viewModel"
									:error-message="cardErrorMessage"
									@upgrade-ready="updateInfo"
									@address-updated="updateSubscriptionCost"
									@tax-info-updated="updateSubscriptionCost"
									@update-payment-method="togglePaymentType"
									@set-errors="setErrors"
									@submit="updatePlan"
								/>
							</template>
							<PlanDetails
								v-if="!subscriptionCostThrottled"
								:view-model="viewModel"
								:cost-model="costModel"
							/>
							<CCallout v-else type="error" class="mb-4">
								We are unable to process your request at the moment. Please try again later.
							</CCallout>
							<div class="change-plan__buttons flex items-center justify-end gap-4 mt-5">
								<CButton
									class="button--secondary"
									@click="cancel"
								>
									Cancel
								</CButton>
								<CToolTip :disabled="!subscriptionCostLoading" placement="top" :visible-arrow="false">
									<template #button>
										<StatusButton
											:status="subscriptionCostLoading || subscriptionCostThrottled ? 'disabled' : changePlanButtonStatus"
											type="submit" class="button--primary"
											@reset-button-status="changePlanButtonStatus = 'default'"
										>
											{{ changePlanButtonText }}
										</StatusButton>
									</template>
									Calculating tax...
								</CToolTip>
							</div>
						</form>
					</ValidationObserver>
				</div>
			</section>
		</div>
	</main>
</template>

<script lang="ts">
	import { defineComponent, onMounted, ref, computed, watch } from 'vue';
	import type { DowngradeFeedbackSurvey } from '@cognitoforms/api/services/subscription-service.ts';
	import { ValidationObserver } from 'vee-validate';
	import PlanDetails from './PlanDetails.vue';
	import CPlanCard from 'src/views/pricing/PlanCard.vue';
	import { submitDowngradeSurvey } from '@cognitoforms/api/services/subscription-service';
	import SubscriptionBilling from '../../../../components/app/SubscriptionBilling.vue';
	import { globalStore } from 'src/stores/global-store';
	import DowngradeSurvey from './DowngradeSurvey.vue';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import StatusButton from 'src/components/interaction/StatusButton.vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import CExpandable from 'src/components/interaction/Expandable.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import { useRouter } from 'vue-router/composables';
	import { useCheckoutBilling } from './use-checkout-billing';

	export default defineComponent({
		components: {
			SubscriptionBilling,
			PlanDetails,
			ValidationObserver,
			CPlanCard,
			DowngradeSurvey,
			StatusButton,
			CCallout,
			CButton,
			CExpandable,
			CToolTip
		},
		props: {
			plan: { type: String, required: true },
			useNewBilling: { type: Boolean, default: false },
			isHipaaCompliant: { type: Boolean, default: false }
		},
		emits: ['loading', 'close', 'change-plan-status', 'handle-close', 'component-loaded'],
		setup(props, { emit }) {
			onMounted(() => emit('component-loaded'));
			const showAllFeaturesLosing = ref(false);
			const billing = useCheckoutBilling({ plan: props.plan, emit });
			const { viewModel } = billing;
			const router = useRouter();

			const downgradeButtonStatus = ref<ButtonStatus>('disabled');
			const paymentMethod = ref(null);
			const isPrepay = computed(() => viewModel.value.PrepaymentMonths > 0);

			const changePlanButtonText = computed(() => {
				if (paymentMethod.value === 'invoice')
					return 'Generate Invoice';
				if (viewModel.value.IsPlanChange || viewModel.value.NewPlanIsIndividual)
					return 'Change Plan';
				if (isPrepay.value)
					return 'Pay Now';

				return 'Change Plan';
			});

			const allowToggleFeaturesLosing = computed(() => viewModel.value.NewPlan.Name === 'Individual');

			const featuresLosing = computed(() => {
				const featuresToRemove = [
					'Bulk Document Download',
					'Resend Email Notifications',
					'Password Field',
					'Workflow Links',
					'Verified Email Domains',
					'Additional Users',
					'Form Folders',
					'Electronic Signatures',
					'Card on File',
					'Bulk Actions',
					'Address Autocomplete',
					'Saved Entry Views'
				];

				const updatedFeatures = viewModel.value.PlanPricingCardViewModel?.FeaturesLosing
					?.filter(f => !featuresToRemove.includes(f))
					.map(f => (['PayPal Payments', 'Square Payments'].includes(f) ? 'Choice of Payment Provider' : f));

				return [...new Set(updatedFeatures)];
			});
			const visibleFeaturesLosing = computed(() => allowToggleFeaturesLosing.value ? featuresLosing.value.slice(0, 5) : featuresLosing.value);
			const overflowFeaturesLosing = computed(() => featuresLosing.value.slice(5));
			const showMoreText = computed(() => showAllFeaturesLosing.value ? 'Show Less' : `Losing ${featuresLosing.value.length} Features`);

			const togglePaymentType = (option) => {
				paymentMethod.value = option;
			};

			watch(() => props.isHipaaCompliant, () => {
				if (props.isHipaaCompliant && props.plan.toLowerCase() !== 'enterprise')
					router.replace(`/${globalStore.organization.Code}/settings/plan`);
			});

			return {
				...billing,
				showAllFeaturesLosing,
				allowToggleFeaturesLosing,
				featuresLosing,
				visibleFeaturesLosing,
				overflowFeaturesLosing,
				showMoreText,
				downgradeButtonStatus,
				isPrepay,
				changePlanButtonText,
				togglePaymentType
			};
		},
		head() {
			return {
				title: 'Change Plan - Cognito Forms',
				titleTemplate: '%s'
			};
		},
		methods: {
			async submitDowngradeFeedback(downgradeData: DowngradeFeedbackSurvey) {
				this.downgradeButtonStatus = 'validating';

				try {
					await submitDowngradeSurvey(downgradeData);
					await this.updateInfo(this.viewModel);
				}
				catch (error) {
					this.downgradeButtonStatus = 'error';
				}
			},
			cancel() {
				if (this.changePlanButtonStatus === 'validating')
					this.$emit('handle-close');
				else
					this.$router.replace({ name: 'organization-settings-plan' });
			}
		}
	});
</script>

<style lang="scss">
.organization-settings-change-plan {

	.change-plan {

		&__container {
			container: changeplan / inline-size;

			> section {
				/* stylelint-disable-next-line scss/at-rule-no-unknown */
				@container changeplan (min-width: #{$b10}) {
					gap: $space__5;
				}
			}

			[data-app-layout] ~ .el-dialog__wrapper & {
				padding-bottom: $space__6;
			}
		}

		&__card {
			display: none;
			width: 275px;
			padding: $space__5;

			/* stylelint-disable-next-line scss/at-rule-no-unknown */
			@container changeplan (min-width: #{$b7}) {
				display: block;
			}

			/* stylelint-disable-next-line scss/at-rule-no-unknown */
			@container changeplan (min-width: #{$b10}) {
				padding: $space__5 $space__6;
			}

			// App Nav
			&.plan {
				padding: 0 $space__5;
			}
		}

		[data-app-layout] ~ .el-dialog__wrapper &__buttons {
			position: absolute;
			bottom: 0;
			right: 0;
		}
	}
}
</style>
